/// ToastService class, manages the toast notifications
// mainly used for storing toast message jsons in localStorage
// and load them in newly rendered pages in App.vue to show the toast(s)
// useToast hook is used in this class

import { ToastOptions } from 'vuestic-ui'

// Toast categories for filtering/clearing specific types of toasts
export type ToastCategory = 'auth' | 'permission' | 'general'

// Extended toast options with category
export interface ToastMessageWithCategory extends ToastOptions {
  category?: ToastCategory
}

const TOAST_STORAGE_KEY = 'toastMessages'

// there can be multiple toast messages already in the localStorage
export class ToastService {
  static storeToastMessageJson(toastMessageJson: ToastMessageWithCategory) {
    const toastMessages = ToastService.loadToastMessagesJsonRaw() || []
    toastMessages.push(toastMessageJson)
    localStorage.setItem(TOAST_STORAGE_KEY, JSON.stringify(toastMessages))
  }

  /**
   * Load and clear all toast messages from localStorage
   */
  static loadToastMessagesJson(): ToastOptions[] | undefined {
    const toastMessages = ToastService.loadToastMessagesJsonRaw()
    if (toastMessages) {
      // clear toast messages from localStorage
      localStorage.removeItem(TOAST_STORAGE_KEY)
      return toastMessages
    }
    return undefined
  }

  /**
   * Load toast messages without clearing them (internal use)
   */
  private static loadToastMessagesJsonRaw(): ToastMessageWithCategory[] | undefined {
    const toastMessageJson = localStorage.getItem(TOAST_STORAGE_KEY)
    if (toastMessageJson) {
      return JSON.parse(toastMessageJson)
    }
    return undefined
  }

  /**
   * Clear toast messages by category without displaying them
   * Useful for clearing stale auth toasts after successful login
   */
  static clearByCategory(category: ToastCategory) {
    const toastMessages = ToastService.loadToastMessagesJsonRaw()
    if (toastMessages) {
      const filteredMessages = toastMessages.filter((msg) => msg.category !== category)
      if (filteredMessages.length > 0) {
        localStorage.setItem(TOAST_STORAGE_KEY, JSON.stringify(filteredMessages))
      } else {
        localStorage.removeItem(TOAST_STORAGE_KEY)
      }
    }
  }

  /**
   * Clear all toast messages without displaying them
   */
  static clearAll() {
    localStorage.removeItem(TOAST_STORAGE_KEY)
  }
}
